// features/feedback/FeedbackWidget.tsx — "Submit feedback", for every role.
//
// A small tab tucked against the right edge of every staff screen: only its
// icon shows until it is hovered or focused, when it slides out to read
// "Feedback". On a touch screen (no hover) the icon itself is the tap
// target. It opens a side panel where staff can type, record a voice note,
// attach a screenshot (or paste one), and see what happened to what they
// sent before ("My feedback", with the ClintoBytes team's reply).
//
// Also opened from the profile menu via openFeedback() (api.ts), so it is
// reachable without a pointer.
//
// What is sent (backend/routes/staff-feedback.js): the text and files, plus
// the screen the user was on, their role and branch, and browser/screen size
// — all stated on the panel. Nothing here is patient data, and the panel
// asks people not to type any in.
import { useCallback, useEffect, useMemo, useRef, useState, type ClipboardEvent } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Bug, HelpCircle, Lightbulb, MessageSquarePlus, Mic, Paperclip, Play, Square, ThumbsUp, Trash2, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { EmptyState, LoadingState } from '@/components/ui/States';
import { cn } from '@/lib/cn';
import { keys } from '@/lib/queryKeys';
import { authFetch } from '@/api';
import { useSegmentedRecorder } from '@/hooks/useSegmentedRecorder';
import { transcribeSegments } from '@/utils/sarvam';
import { getVoiceLanguage } from '@/lib/voiceLanguages';
import { useAiAvailability } from '@/features/ai-assist/useAiAvailability';
import {
  OPEN_FEEDBACK_EVENT, submitFeedback, useMyFeedback, type FeedbackPrefill,
  type FeedbackCategory, type FeedbackStatus,
} from './api';

const MAX_SECONDS = 300;

const CATEGORIES: { id: FeedbackCategory; label: string; icon: typeof Bug }[] = [
  { id: 'bug', label: 'Something is wrong', icon: Bug },
  { id: 'idea', label: 'Suggestion', icon: Lightbulb },
  { id: 'question', label: 'Question', icon: HelpCircle },
  { id: 'praise', label: 'I like this', icon: ThumbsUp },
];

const CATEGORY_LABEL: Record<FeedbackCategory, string> = {
  bug: 'Problem', idea: 'Suggestion', question: 'Question', praise: 'Praise', other: 'Other',
};

const STATUS_BADGE: Record<FeedbackStatus, { label: string; variant: 'neutral' | 'info' | 'success' | 'warn' }> = {
  new: { label: 'Received', variant: 'neutral' },
  reviewing: { label: 'Being reviewed', variant: 'info' },
  converted: { label: 'Planned', variant: 'warn' },
  resolved: { label: 'Done', variant: 'success' },
  dismissed: { label: 'Closed', variant: 'neutral' },
};

function clock(sec: number) {
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
}

/** Plays the recorder's segments back to back through one <audio> element. */
function useSegmentPlayer(segments: Blob[]) {
  const urls = useMemo(() => segments.map(s => URL.createObjectURL(s)), [segments]);
  useEffect(() => () => urls.forEach(u => URL.revokeObjectURL(u)), [urls]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const indexRef = useRef(0);
  const [playing, setPlaying] = useState(false);

  const stop = useCallback(() => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlaying(false);
  }, []);

  const playFrom = useCallback((i: number) => {
    const url = urls[i];
    if (!url) { stop(); return; }
    indexRef.current = i;
    const a = new Audio(url);
    audioRef.current = a;
    a.onended = () => playFrom(indexRef.current + 1);
    void a.play().catch(() => stop());
  }, [urls, stop]);

  const toggle = useCallback(() => {
    if (playing) stop();
    else { setPlaying(true); playFrom(0); }
  }, [playing, playFrom, stop]);

  useEffect(() => stop, [stop, urls]);
  return { playing, toggle, stop };
}

function SendTab({ moduleLabel, onSent, prefill }: { moduleLabel?: string; onSent: (ref: string) => void; prefill?: FeedbackPrefill | null }) {
  const { aiEnabled } = useAiAvailability();
  const qc = useQueryClient();
  const recorder = useSegmentedRecorder();
  const [category, setCategory] = useState<FeedbackCategory>(prefill?.category ?? 'bug');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState(prefill?.message ?? '');
  const [transcript, setTranscript] = useState('');
  const [segments, setSegments] = useState<Blob[]>([]);
  const [seconds, setSeconds] = useState(0);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [busy, setBusy] = useState<'' | 'transcribing' | 'sending'>('');
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement | null>(null);
  const player = useSegmentPlayer(segments);

  // Closing the panel unmounts this form; a recording still running must
  // release the microphone rather than keep it open in the background.
  const recorderRef = useRef(recorder);
  useEffect(() => { recorderRef.current = recorder; });
  useEffect(() => () => { if (recorderRef.current.recording) recorderRef.current.stop(); }, []);

  const stopRecording = useCallback(async () => {
    const elapsed = recorder.timeElapsed;
    const segs = await recorder.stopAndWait();
    setSegments(segs);
    setSeconds(elapsed);
  }, [recorder]);

  // A voice note is capped at five minutes.
  useEffect(() => {
    if (recorder.recording && recorder.timeElapsed >= MAX_SECONDS) void stopRecording();
  }, [recorder.recording, recorder.timeElapsed, stopRecording]);

  const startRecording = async () => {
    setError(''); player.stop(); setSegments([]); setTranscript('');
    await recorder.start();
  };

  const removeRecording = () => {
    player.stop(); recorder.reset(); setSegments([]); setSeconds(0); setTranscript('');
  };

  const transcribe = async () => {
    setBusy('transcribing'); setError('');
    try {
      const lang = getVoiceLanguage();
      let text = await transcribeSegments(segments, lang, 'feedback');
      if (text && lang !== 'en-IN') {
        const res = await authFetch('/api/voice/translate', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, source_language_code: lang, target_language_code: 'en-IN' }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.translated_text) text = data.translated_text;
      }
      if (!text) setError('No speech was detected in the recording.');
      else {
        setTranscript(text);
        setMessage(m => (m.trim() ? `${m.trim()}\n\n${text}` : text));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Transcription failed');
    }
    setBusy('');
  };

  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const item = Array.from(e.clipboardData.items).find(i => i.type.startsWith('image/'));
    const file = item?.getAsFile();
    if (file) {
      e.preventDefault();
      setAttachment(new File([file], `pasted-screenshot.${file.type.split('/')[1] || 'png'}`, { type: file.type }));
    }
  };

  const pickFile = (f: File | undefined) => {
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { setError('The attachment is larger than 8 MB.'); return; }
    setError(''); setAttachment(f);
  };

  const canSend = !busy && !recorder.recording && (message.trim().length > 0 || segments.length > 0);

  const send = async () => {
    setBusy('sending'); setError('');
    try {
      const { ref } = await submitFeedback({
        category, title: title.trim() || undefined, message: message.trim() || undefined,
        transcript: transcript || undefined, module: moduleLabel,
        audio: segments, audioSeconds: seconds, attachment,
      });
      void qc.invalidateQueries({ queryKey: keys.feedback.mine() });
      onSent(ref);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send feedback');
    }
    setBusy('');
  };

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-4" onPaste={onPaste}>
      <div>
        <div className="mb-1.5 text-[12px] font-semibold text-ink">What is it about?</div>
        <div className="grid grid-cols-2 gap-2">
          {CATEGORIES.map(c => {
            const Icon = c.icon;
            const active = category === c.id;
            return (
              <button
                key={c.id} type="button" onClick={() => setCategory(c.id)} aria-pressed={active}
                className={cn(
                  'flex items-center gap-2 rounded-md border px-3 py-2 text-left text-[12.5px] transition-colors',
                  active ? 'border-teal bg-teal-light font-semibold text-ink' : 'border-border bg-transparent text-ink-muted hover:bg-muted',
                )}
              >
                <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />{c.label}
              </button>
            );
          })}
        </div>
      </div>

      <label className="block">
        <span className="mb-1 block text-[12px] font-semibold text-ink">Subject <span className="font-normal text-ink-muted">(optional)</span></span>
        <Input value={title} onChange={e => setTitle(e.target.value)} maxLength={200} placeholder="e.g. Bill print shows the wrong GST" />
      </label>

      <label className="block">
        <span className="mb-1 block text-[12px] font-semibold text-ink">Tell us more</span>
        <Textarea
          value={message} onChange={e => setMessage(e.target.value)} rows={5} maxLength={5000}
          placeholder="What happened, what you expected, or what would make your work easier…"
        />
      </label>

      <div className="rounded-md border border-border p-3">
        <div className="mb-2 text-[12px] font-semibold text-ink">Or say it — voice note</div>
        {recorder.recording ? (
          <div className="flex items-center gap-3">
            <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-danger" aria-hidden="true" />
            <span className="text-[13px] font-semibold tabular-nums text-ink">{clock(recorder.timeElapsed)}</span>
            <span className="text-[11px] text-ink-muted">of {clock(MAX_SECONDS)}</span>
            <Button type="button" size="sm" variant="danger" className="ml-auto" onClick={() => void stopRecording()}>
              <Square className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Stop
            </Button>
          </div>
        ) : segments.length ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={player.toggle}>
              {player.playing ? <Square className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> : <Play className="mr-1 h-3.5 w-3.5" aria-hidden="true" />}
              {player.playing ? 'Stop' : 'Play'}
            </Button>
            <span className="text-[12.5px] text-ink">Voice note · {clock(seconds)}</span>
            {aiEnabled && (
              <Button type="button" size="sm" variant="ghost" loading={busy === 'transcribing'} disabled={!!busy} onClick={() => void transcribe()}>
                Transcribe into message
              </Button>
            )}
            <Button type="button" size="sm" variant="link" className="ml-auto text-danger" onClick={removeRecording}>
              <Trash2 className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Remove
            </Button>
          </div>
        ) : (
          <Button type="button" size="sm" variant="ghost" onClick={() => void startRecording()}>
            <Mic className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Record a voice note
          </Button>
        )}
        {recorder.error && <div className="mt-2 text-[11.5px] text-danger-ink">{recorder.error}</div>}
      </div>

      <div>
        <input
          ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,application/pdf" className="hidden"
          onChange={e => { pickFile(e.target.files?.[0]); e.target.value = ''; }}
        />
        {attachment ? (
          <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-[12.5px] text-ink">
            <Paperclip className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate">{attachment.name}</span>
            <button type="button" aria-label="Remove attachment" onClick={() => setAttachment(null)} className="text-ink-muted hover:text-ink">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <Button type="button" size="sm" variant="ghost" onClick={() => fileRef.current?.click()}>
            <Paperclip className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Attach a screenshot
          </Button>
        )}
        <div className="mt-1 text-[11px] text-ink-muted">You can also paste a screenshot here (Ctrl+V).</div>
      </div>

      <div className="rounded-md bg-warn-soft px-3 py-2 text-[11.5px] leading-snug text-warn-ink">
        Please don't include patient names, UHIDs or other patient details.
      </div>
      <div className="text-[11px] leading-snug text-ink-muted">
        Sent with your name, role and branch, the screen you're on{moduleLabel ? ` (${moduleLabel})` : ''} and your browser
        details, so the ClintoBytes team can follow up.
      </div>

      {error && <div className="rounded-md bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{error}</div>}

      <Button type="button" full disabled={!canSend} loading={busy === 'sending'} onClick={() => void send()}>
        Send feedback
      </Button>
    </div>
  );
}

function HistoryTab({ active }: { active: boolean }) {
  const q = useMyFeedback(active);
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <EmptyState label="Your feedback could not be loaded." />;
  const items = q.data ?? [];
  if (!items.length) return <EmptyState label="You haven't sent any feedback yet." />;
  return (
    <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-5 py-4">
      {items.map(f => {
        const s = STATUS_BADGE[f.status];
        return (
          <div key={f.id} className="rounded-md border border-border p-3">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[11px] text-ink-muted">{f.ref}</span>
              <span className="text-[11px] text-ink-muted">· {CATEGORY_LABEL[f.category]} · {new Date(f.created_at).toLocaleDateString()}</span>
              <Badge variant={s.variant} className="ml-auto">{s.label}</Badge>
            </div>
            <div className="mt-1 line-clamp-3 text-[12.5px] text-ink">
              {f.title || f.message || (f.audio_parts ? 'Voice note' : '')}
            </div>
            {f.reply_to_user && (
              <div className="mt-2 rounded-md bg-info-soft px-2.5 py-2 text-[12px] text-info-ink">
                <span className="font-semibold">ClintoBytes team: </span>{f.reply_to_user}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function FeedbackWidget({ moduleLabel }: { moduleLabel?: string }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'send' | 'history'>('send');
  const [sentRef, setSentRef] = useState<string | null>(null);
  const [formKey, setFormKey] = useState(0);
  const [prefill, setPrefill] = useState<FeedbackPrefill | null>(null);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<FeedbackPrefill | null>).detail;
      setOpen(true); setTab('send');
      // A pre-filled opening starts a fresh form with that text.
      if (detail) { setPrefill(detail); setSentRef(null); setFormKey(k => k + 1); }
    };
    window.addEventListener(OPEN_FEEDBACK_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_FEEDBACK_EVENT, onOpen);
  }, []);

  const close = (next: boolean) => {
    setOpen(next);
    if (!next && sentRef) { setSentRef(null); setFormKey(k => k + 1); }
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={close}>
      <DialogPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label="Send feedback"
          className={cn(
            'group fixed right-0 top-[58%] z-[150] flex items-center gap-2 rounded-l-lg bg-teal py-2 pl-2.5 pr-3 text-white shadow-lg print:hidden',
            'translate-x-[calc(100%-36px)] opacity-80 transition-all duration-200 ease-out',
            'hover:translate-x-0 hover:opacity-100 focus-visible:translate-x-0 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            open && 'translate-x-0 opacity-100',
          )}
        >
          <MessageSquarePlus className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="whitespace-nowrap text-[12.5px] font-semibold">Feedback</span>
        </button>
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[200] bg-black/30 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          // A stray click beside the panel must not throw away a half-written
          // note or a recording; it closes with ✕ or Esc.
          onInteractOutside={e => e.preventDefault()}
          className={cn(
            'fixed inset-y-0 right-0 z-[200] flex w-full max-w-[440px] flex-col border-l border-border bg-card shadow-xl outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
          )}
        >
          <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
            <div>
              <DialogPrimitive.Title className="text-[15px] font-semibold text-ink">Send feedback</DialogPrimitive.Title>
              <DialogPrimitive.Description className="mt-0.5 text-[12px] text-ink-muted">
                Report a problem or suggest an improvement — it goes straight to the ClintoBytes team.
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close aria-label="Close" className="rounded-full p-1 text-ink-muted hover:bg-muted hover:text-ink">
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>

          <div className="flex gap-1 border-b border-line px-5 pt-2" role="tablist">
            {(['send', 'history'] as const).map(t => (
              <button
                key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
                className={cn(
                  '-mb-px rounded-none border-0 border-b-2 bg-transparent px-3 pb-2 text-[12.5px] font-semibold',
                  tab === t ? 'border-teal text-ink' : 'border-transparent text-ink-muted hover:text-ink',
                )}
              >
                {t === 'send' ? 'New feedback' : 'My feedback'}
              </button>
            ))}
          </div>

          {tab === 'history' ? (
            <HistoryTab active={open && tab === 'history'} />
          ) : sentRef ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
              <div className="text-[28px]" aria-hidden="true">🙏</div>
              <div className="text-[15px] font-semibold text-ink">Thank you — feedback sent</div>
              <div className="text-[12.5px] text-ink-muted">
                Reference <span className="font-mono font-semibold text-ink">{sentRef}</span>. You can follow it under My feedback.
              </div>
              <div className="mt-2 flex gap-2">
                <Button variant="ghost" onClick={() => setTab('history')}>My feedback</Button>
                <Button onClick={() => { setSentRef(null); setFormKey(k => k + 1); }}>Send another</Button>
              </div>
            </div>
          ) : (
            <SendTab key={formKey} moduleLabel={moduleLabel} onSent={(ref) => { setSentRef(ref); setPrefill(null); }} prefill={prefill} />
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
